import { useEffect, useMemo, useState } from "react";
import {
  isLocallyOwnedUpload,
  isWebLocksSupported,
  queryUploadOwnership,
} from "@/utils/uploadOwnership";

const POLL_INTERVAL_MS = 1000;
const NONE_GRACE_MS = 3000;

const seenOtherByUpload = new Set();
const noneAtByUpload = new Map();

const initialOwnershipFor = (uploadId) => {
  if (!uploadId) return "unsupported";
  if (!isWebLocksSupported()) return "unsupported";
  if (isLocallyOwnedUpload(uploadId)) return "me";
  return "unknown";
};

export const useUploadOwnership = (uploadId) => {
  const [ownership, setOwnership] = useState(() => initialOwnershipFor(uploadId));

  useEffect(() => {
    if (!uploadId) {
      setOwnership("unsupported");
      return undefined;
    }
    setOwnership(initialOwnershipFor(uploadId));

    let cancelled = false;

    const tick = async () => {
      const next = await queryUploadOwnership(uploadId);
      if (!cancelled) {
        setOwnership(next);
      }
    };

    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [uploadId]);

  return ownership;
};

const initialHasOwnedFor = (uploadIds) => {
  if (!uploadIds || uploadIds.length === 0) return false;
  if (!isWebLocksSupported()) return true;
  return uploadIds.some((id) => isLocallyOwnedUpload(id));
};

export const useHasOwnedActiveUpload = (uploadIds) => {
  const stableUploadIds = useMemo(() => uploadIds || [], [uploadIds]);
  const [hasOwned, setHasOwned] = useState(() => initialHasOwnedFor(stableUploadIds));
  const idsKey = stableUploadIds.join(",");

  useEffect(() => {
    if (stableUploadIds.length === 0) {
      setHasOwned(false);
      return undefined;
    }

    let cancelled = false;

    const tick = async () => {
      const results = await Promise.all(stableUploadIds.map((id) => queryUploadOwnership(id)));
      if (cancelled) return;
      const now = Date.now();
      stableUploadIds.forEach((id, i) => {
        const r = results[i];
        if (r === "other") {
          seenOtherByUpload.add(id);
          noneAtByUpload.delete(id);
        } else if (r === "none") {
          if (seenOtherByUpload.has(id) && !noneAtByUpload.has(id)) {
            noneAtByUpload.set(id, now);
          }
        } else {
          noneAtByUpload.delete(id);
        }
      });
      const canOwn = stableUploadIds.some((id, i) => {
        const r = results[i];
        if (r === "me" || r === "unsupported") return true;
        if (r === "none") {
          if (!seenOtherByUpload.has(id)) return true;
          const noneAt = noneAtByUpload.get(id);
          return !!noneAt && now - noneAt >= NONE_GRACE_MS;
        }
        return false;
      });
      setHasOwned(canOwn);
    };

    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [idsKey, stableUploadIds]);

  return hasOwned;
};